import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  Library,
  Layers,
  ImageIcon,
  TrendingUp,
  FileSearch,
  Workflow,
  Settings,
  Loader2,
  AlertCircle,
  Sparkles,
  Star,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { useFrameworksStore } from '@/stores/frameworks';
import type {
  CreativeFrameworkV2 as CreativeFramework,
  FrameworkProposal,
  FrameworkResearchReport,
  FrameworkSegment,
  FrameworkReferenceAd as ReferenceAd,
} from '@/services/api';

type TabKey =
  | 'structure'
  | 'reference_ads'
  | 'performance'
  | 'research'
  | 'application'
  | 'guidance';

const TABS: Array<{ key: TabKey; label: string; icon: React.ReactNode }> = [
  { key: 'structure', label: 'Structure', icon: <Layers size={14} /> },
  { key: 'reference_ads', label: 'Reference Ads', icon: <ImageIcon size={14} /> },
  { key: 'performance', label: 'Performance', icon: <TrendingUp size={14} /> },
  { key: 'research', label: 'Research', icon: <FileSearch size={14} /> },
  { key: 'application', label: 'Application', icon: <Workflow size={14} /> },
  { key: 'guidance', label: 'Guidance', icon: <Settings size={14} /> },
];

export function FrameworkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const {
    selected,
    selectedLoading,
    selectedError,
    fetchOne,
    fetchReports,
    fetchReferenceAds,
    triggerResearch,
    triggeringResearch,
  } = useFrameworksStore();
  const [tab, setTab] = useState<TabKey>('structure');

  useEffect(() => {
    if (!id) return;
    fetchOne(id);
    fetchReports(id);
    fetchReferenceAds(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (selectedLoading && !selected) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" /> Loading framework…
      </div>
    );
  }

  if (selectedError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
        <AlertCircle size={16} />
        <span>{selectedError}</span>
      </div>
    );
  }

  if (!selected || !id) {
    return (
      <div className="text-center text-sm text-slate-400 py-16">
        Framework not found.{' '}
        <Link to="/frameworks" className="text-brand-coral hover:underline">
          Back to library
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div className="space-y-2">
          <Link
            to="/frameworks"
            className="text-xs text-slate-500 hover:text-navy-900 inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Library
          </Link>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Library size={22} className="text-brand-coral" />
            {selected.canonical_name}
          </h1>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <code className="bg-slate-100 px-1.5 py-0.5 rounded">{selected.framework_id}</code>
            <span>v{selected.version}</span>
            <span>•</span>
            <span>{selected.source_category.replaceAll('_', ' ')}</span>
            {selected.originator && (
              <>
                <span>•</span>
                <span>{selected.originator}</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => triggerResearch(id)}
          disabled={triggeringResearch}
          className="flex items-center gap-1.5 text-xs font-medium bg-brand-coral text-white px-3 py-2 rounded-lg hover:bg-brand-coral/90 disabled:opacity-50"
        >
          {triggeringResearch ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Sparkles size={14} />
          )}
          {triggeringResearch ? 'Researching…' : 'Run Research'}
        </button>
      </div>

      <div className="border-b border-slate-200 flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors',
              tab === t.key
                ? 'border-brand-coral text-navy-900'
                : 'border-transparent text-slate-500 hover:text-navy-900',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'structure' && <StructureTab framework={selected} />}
        {tab === 'reference_ads' && <ReferenceAdsTab frameworkId={id} />}
        {tab === 'performance' && <PerformanceTab framework={selected} />}
        {tab === 'research' && <ResearchTab frameworkId={id} />}
        {tab === 'application' && <ApplicationTab framework={selected} />}
        {tab === 'guidance' && <GuidanceTab framework={selected} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Structure
// ---------------------------------------------------------------------------

function StructureTab({ framework }: { framework: CreativeFramework }) {
  return (
    <div className="space-y-3">
      {framework.segments.length === 0 ? (
        <p className="text-sm text-slate-400 italic">
          No segments defined yet. Run research to enrich this framework.
        </p>
      ) : (
        framework.segments.map((seg, idx) => (
          <SegmentEditor key={seg.segment_key} idx={idx} framework={framework} segment={seg} />
        ))
      )}
    </div>
  );
}

function SegmentEditor({
  idx,
  framework,
  segment,
}: {
  idx: number;
  framework: CreativeFramework;
  segment: FrameworkSegment;
}) {
  const { updateFramework } = useFrameworksStore();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    prompt_guidance: segment.prompt_guidance ?? '',
    tone_notes: segment.tone_notes ?? '',
  });

  useEffect(() => {
    setDraft({
      prompt_guidance: segment.prompt_guidance ?? '',
      tone_notes: segment.tone_notes ?? '',
    });
  }, [segment.segment_key, segment.prompt_guidance, segment.tone_notes]);

  const save = async () => {
    const updated = framework.segments.map((s, i) =>
      i === idx ? { ...s, prompt_guidance: draft.prompt_guidance, tone_notes: draft.tone_notes || null } : s,
    );
    await updateFramework(framework.framework_id, {
      segments: updated,
      change_summary: `Edited ${segment.segment_name} segment guidance`,
    });
    setEditing(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] text-slate-400 uppercase">Segment {idx + 1}</div>
          <h3 className="text-sm font-semibold text-navy-900">
            {segment.segment_name}
            <code className="ml-2 text-[10px] bg-slate-100 px-1 py-0.5 rounded text-slate-500">
              {segment.segment_key}
            </code>
          </h3>
        </div>
        <div className="text-xs text-slate-500">
          {segment.duration_range
            ? `${segment.duration_range.min}-${segment.duration_range.max} ${segment.duration_range.unit}`
            : '—'}
          {segment.required ? ' • required' : ' • optional'}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase text-slate-500 font-semibold">
            Prompt Guidance
          </label>
          {!editing ? (
            <button
              className="text-[10px] text-brand-coral hover:underline"
              onClick={() => setEditing(true)}
            >
              edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                className="text-[10px] text-slate-500 hover:underline"
                onClick={() => {
                  setEditing(false);
                  setDraft({
                    prompt_guidance: segment.prompt_guidance ?? '',
                    tone_notes: segment.tone_notes ?? '',
                  });
                }}
              >
                cancel
              </button>
              <button
                className="text-[10px] text-emerald-600 hover:underline font-semibold"
                onClick={save}
              >
                save
              </button>
            </div>
          )}
        </div>
        {editing ? (
          <textarea
            className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:border-brand-coral"
            rows={6}
            value={draft.prompt_guidance}
            onChange={(e) => setDraft((d) => ({ ...d, prompt_guidance: e.target.value }))}
          />
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {segment.prompt_guidance || (
              <span className="text-slate-400 italic">No guidance.</span>
            )}
          </p>
        )}
      </div>

      {(segment.tone_notes || editing) && (
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold">Tone</label>
          {editing ? (
            <input
              type="text"
              className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:border-brand-coral"
              value={draft.tone_notes}
              onChange={(e) => setDraft((d) => ({ ...d, tone_notes: e.target.value }))}
            />
          ) : (
            <p className="text-sm text-slate-700">{segment.tone_notes}</p>
          )}
        </div>
      )}

      {segment.common_mistakes.length > 0 && (
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold">Common Mistakes</label>
          <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
            {segment.common_mistakes.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {segment.example_copy.length > 0 && (
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold">Example Lines</label>
          <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
            {segment.example_copy.map((ex, i) => (
              <li key={i} className="italic">
                "{ex}"
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reference Ads
// ---------------------------------------------------------------------------

function ReferenceAdsTab({ frameworkId }: { frameworkId: string }) {
  const { referenceAds, referenceAdsLoading, fetchReferenceAds, toggleFeatureReferenceAd } =
    useFrameworksStore();
  const [featuredOnly, setFeaturedOnly] = useState(false);

  useEffect(() => {
    fetchReferenceAds(frameworkId, featuredOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameworkId, featuredOnly]);

  if (referenceAdsLoading && referenceAds.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading reference ads…
      </div>
    );
  }

  if (referenceAds.length === 0) {
    return (
      <div className="text-center text-sm text-slate-400 py-12 bg-slate-50 rounded-xl">
        <ImageIcon size={32} className="mx-auto text-slate-300 mb-2" />
        <p>No reference ads resolved to this framework yet.</p>
        <p className="text-xs mt-1">
          Run <code className="text-brand-coral">scripts/backfill_framework_resolution.py</code>{' '}
          to populate.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{referenceAds.length} reference ads</p>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={featuredOnly}
            onChange={(e) => setFeaturedOnly(e.target.checked)}
          />
          Featured only
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {referenceAds.map((ad) => (
          <ReferenceAdCard
            key={ad.ad_id}
            ad={ad}
            onToggleFeature={() =>
              toggleFeatureReferenceAd(frameworkId, ad.ad_id, !ad.is_featured)
            }
          />
        ))}
      </div>
    </div>
  );
}

function ReferenceAdCard({
  ad,
  onToggleFeature,
}: {
  ad: ReferenceAd;
  onToggleFeature: () => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2">
      {ad.thumbnail_url && (
        <img
          src={ad.thumbnail_url}
          alt={ad.headline || ad.ad_id}
          className="w-full h-24 object-cover rounded-lg"
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-navy-900 truncate">
            {ad.headline || ad.ad_id}
          </div>
          <code className="text-[10px] text-slate-400">{ad.ad_id}</code>
        </div>
        <button
          onClick={onToggleFeature}
          className={clsx(
            'p-1 rounded transition-colors',
            ad.is_featured
              ? 'text-amber-500'
              : 'text-slate-300 hover:text-amber-500',
          )}
          title={ad.is_featured ? 'Unfeature' : 'Feature'}
        >
          <Star size={14} fill={ad.is_featured ? 'currentColor' : 'none'} />
        </button>
      </div>
      {ad.transcript_preview && (
        <p className="text-[10px] text-slate-500 line-clamp-2">{ad.transcript_preview}</p>
      )}
      <div className="flex items-center gap-2 text-[10px] text-slate-500">
        {ad.framework_confidence !== undefined && (
          <span>conf {(ad.framework_confidence * 100).toFixed(0)}%</span>
        )}
        {ad.resolution_method && <span>via {ad.resolution_method}</span>}
        {ad.performance_tier && (
          <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
            {ad.performance_tier}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Performance
// ---------------------------------------------------------------------------

function PerformanceTab({ framework }: { framework: CreativeFramework }) {
  const scenarios = Object.values(framework.scenario_performance);
  const platforms = Object.values(framework.platform_performance);
  if (scenarios.length === 0 && platforms.length === 0) {
    return (
      <div className="text-center text-sm text-slate-400 py-12 bg-slate-50 rounded-xl">
        <TrendingUp size={32} className="mx-auto text-slate-300 mb-2" />
        <p>Performance data populates as ads run with this framework.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {scenarios.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">
            By Buyer Scenario
          </h3>
          <div className="bg-white rounded-xl border border-slate-200">
            {scenarios.map((m) => (
              <div
                key={m.scenario_id}
                className="flex items-center justify-between px-4 py-2 border-b border-slate-100 last:border-0"
              >
                <span className="text-xs">{m.scenario_id}</span>
                <span className="text-xs">
                  ROAS {m.roas_index.toFixed(2)}× • CTR {m.ctr_index.toFixed(2)}× • n={m.sample_n}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {platforms.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">By Platform</h3>
          <div className="bg-white rounded-xl border border-slate-200">
            {platforms.map((m) => (
              <div
                key={m.platform}
                className="flex items-center justify-between px-4 py-2 border-b border-slate-100 last:border-0"
              >
                <span className="text-xs">{m.platform}</span>
                <span className="text-xs">
                  {m.roas_index !== null ? `ROAS ${m.roas_index.toFixed(2)}× •` : ''}{' '}
                  CTR {m.ctr_index.toFixed(2)}× • n={m.sample_n}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Research
// ---------------------------------------------------------------------------

function ResearchTab({ frameworkId }: { frameworkId: string }) {
  const { reports, reportsLoading, fetchReports, approveReport, rejectReport } =
    useFrameworksStore();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    fetchReports(frameworkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameworkId]);

  if (reportsLoading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading research reports…
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="text-center text-sm text-slate-400 py-12 bg-slate-50 rounded-xl">
        <FileSearch size={32} className="mx-auto text-slate-300 mb-2" />
        <p>No research reports yet. Click "Run Research" to generate one.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reports.map((report) => (
        <ReportCard
          key={report.report_id}
          report={report}
          expanded={expandedId === report.report_id}
          onToggle={() =>
            setExpandedId((prev) => (prev === report.report_id ? null : report.report_id))
          }
          onApprove={(proposalIds) =>
            approveReport(frameworkId, report.report_id, proposalIds)
          }
          onReject={() => rejectReport(frameworkId, report.report_id)}
        />
      ))}
    </div>
  );
}

const REPORT_STATUS_COLORS: Record<string, string> = {
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-sky-100 text-sky-800',
  applied: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-slate-100 text-slate-500',
};

function ReportCard({
  report,
  expanded,
  onToggle,
  onApprove,
  onReject,
}: {
  report: FrameworkResearchReport;
  expanded: boolean;
  onToggle: () => void;
  onApprove: (proposalIds?: string[]) => void;
  onReject: () => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'px-2 py-0.5 text-[10px] font-medium rounded',
              REPORT_STATUS_COLORS[report.status],
            )}
          >
            {report.status.replace('_', ' ')}
          </span>
          <span className="text-xs text-slate-500">
            {report.triggered_by} • {new Date(report.created_at).toLocaleString()}
          </span>
        </div>
        <div className="text-xs text-slate-600">
          {report.proposed_changes.length} proposals (
          <span className="text-emerald-600">{report.auto_approved_count} auto</span>,{' '}
          <span className="text-amber-600">{report.pending_review_count} review</span>)
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {report.proposed_changes.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No proposals.</p>
          ) : (
            report.proposed_changes.map((p) => <ProposalDiffCard key={p.proposal_id} proposal={p} />)
          )}
          {report.status === 'pending_review' && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={onReject}
                className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-rose-600 px-3 py-1.5 rounded-lg"
              >
                <XCircle size={12} /> Reject
              </button>
              <button
                onClick={() => onApprove()}
                className="flex items-center gap-1 text-xs font-medium bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700"
              >
                <CheckCircle2 size={12} /> Approve all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProposalDiffCard({ proposal }: { proposal: FrameworkProposal }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3 text-xs">
      <div className="flex items-center justify-between mb-1">
        <code className="text-[10px] text-slate-500">{proposal.field_path}</code>
        <span
          className={clsx(
            'px-1.5 py-0.5 text-[10px] font-medium rounded',
            proposal.proposal_type === 'minor'
              ? 'bg-sky-100 text-sky-700'
              : 'bg-amber-100 text-amber-800',
          )}
        >
          {proposal.proposal_type}
          {proposal.auto_approved && ' • auto-approved'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] text-slate-400 mb-0.5">CURRENT</div>
          <pre className="bg-white border border-slate-200 rounded p-1.5 whitespace-pre-wrap break-words text-[11px]">
            {formatValue(proposal.current_value)}
          </pre>
        </div>
        <div>
          <div className="text-[10px] text-emerald-600 mb-0.5">PROPOSED</div>
          <pre className="bg-emerald-50 border border-emerald-200 rounded p-1.5 whitespace-pre-wrap break-words text-[11px]">
            {formatValue(proposal.proposed_value)}
          </pre>
        </div>
      </div>
      {proposal.rationale && (
        <p className="text-[11px] text-slate-600 mt-2 italic">
          {proposal.rationale} (confidence {(proposal.confidence * 100).toFixed(0)}%)
        </p>
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '(empty)';
  if (typeof v === 'string') return v;
  return JSON.stringify(v, null, 2);
}

// ---------------------------------------------------------------------------
// Application
// ---------------------------------------------------------------------------

function ApplicationTab({ framework }: { framework: CreativeFramework }) {
  return (
    <div className="space-y-4">
      <Section title="Best for archetypes" items={framework.best_for_archetypes} />
      <Section title="Contraindicated for" items={framework.contraindicated_for} />
      <Section title="Ideal hook types" items={framework.ideal_hook_types} />
      <Section title="Ideal proof types" items={framework.ideal_proof_types} />
      <Section title="Platforms" items={framework.platforms} />
      <Section title="Funnel stages" items={framework.funnel_stages} />
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">
          Usage Counters
        </h3>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-2xl font-bold text-navy-900">
              {framework.client_ad_count}
            </div>
            <div className="text-xs text-slate-500">client ads</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-navy-900">
              {framework.extracted_ad_count}
            </div>
            <div className="text-xs text-slate-500">wild-extracted ads</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-navy-900">
              {framework.research_report_ids.length}
            </div>
            <div className="text-xs text-slate-500">research reports</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">{title}</h3>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => (
          <span
            key={s}
            className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full"
          >
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Guidance
// ---------------------------------------------------------------------------

function GuidanceTab({ framework }: { framework: CreativeFramework }) {
  return (
    <div className="space-y-4">
      {framework.persona_fit_notes && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">
            Persona Fit Notes
          </h3>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {framework.persona_fit_notes}
          </p>
        </div>
      )}
      {framework.key_findings.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">
            Key Findings
          </h3>
          <ul className="space-y-1 text-sm text-slate-700 list-disc list-inside">
            {framework.key_findings.map((kf, i) => (
              <li key={i}>{kf}</li>
            ))}
          </ul>
        </div>
      )}
      {framework.pairing_recommendations.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">
            Pairing Recommendations
          </h3>
          <div className="space-y-2">
            {framework.pairing_recommendations.map((p, i) => (
              <div key={i} className="text-sm text-slate-700">
                <Link
                  to={`/frameworks/${p.framework_id}`}
                  className="font-semibold text-navy-900 hover:text-brand-coral"
                >
                  {p.framework_id}
                </Link>{' '}
                <span className="text-xs text-slate-400">({p.pairing_position})</span>
                <p className="text-xs text-slate-600">{p.pairing_note}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {framework.refinement_history.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">
            Refinement History
          </h3>
          <div className="space-y-1.5">
            {framework.refinement_history.slice().reverse().slice(0, 10).map((r, i) => (
              <div key={i} className="text-xs text-slate-600 flex items-start gap-2">
                <Clock size={10} className="mt-0.5 text-slate-400" />
                <div>
                  <span className="font-mono text-[10px] text-slate-400 mr-2">v{r.version}</span>
                  {r.change_summary}{' '}
                  <span className="text-slate-400">
                    — {new Date(r.date).toLocaleString()} by {r.triggered_by}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
